import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionsDeprecated } from '@/types/Select'
import { useQuery } from '@apollo/client'
import { GET_GROUPS } from '@/modules/API/daycare/groupHandler'
import { useAuth } from '@/modules/context/AuthContext'
import { ChildDaycareWithParents } from '@/types/Child'
import { Group } from '@/types/Group'
import Loading from '@/components/Loading/Loading'
import { useDialog } from '@/hooks/useDialog/useDialog'
import Groups from '@/container/daycare/daycareProfile/settings/modals/group/group'

interface PriorityGroupSelectorProps {
    daycareChild: ChildDaycareWithParents
    selectValue: string
    setSelectValue: (value: string) => void
}

const PriorityGroupSelector: FunctionComponent<PriorityGroupSelectorProps> = ({
    daycareChild,
    selectValue,
    setSelectValue
}: PriorityGroupSelectorProps) => {
    const { t } = useTranslation()
    const { user } = useAuth()
    const { data, loading, refetch } = useQuery<{ getGroups: Group[] }>(GET_GROUPS, {
        variables: { daycare_id: daycareChild.daycareId }
    })

    const { dialog, reopenTrigger, openDialogActionItem } = useDialog({
        dialogTitle: 'myPriorityGroup',
        Component: Groups,
        resourceLabel: { type: 'manageResource', label: 'myPriorityGroup' }
    })

    const canUpdate: boolean = !!user?.currentDaycareRights.canWriteFamilies
    const isRpeAndHaveNanny: boolean = daycareChild.isRpeOrLaep && daycareChild.parents.some((parent) => parent.isNanny)

    const selectOptions: SelectOptionsDeprecated = [
        { options: [{ name: t('noPriorityGroup'), value: '0' }] },
        {
            options: (data?.getGroups ?? []).map((group) => ({
                value: group.id.toString(),
                name: group.name
            }))
        }
    ]

    if (loading) return <Loading overall />
    if (!data) return null

    return (
        <>
            <SelectUnique
                selectOptions={selectOptions}
                selectValue={selectValue}
                setSelectValue={setSelectValue}
                readOnly={!canUpdate || isRpeAndHaveNanny}
                label={t('priorityGroup')}
                fullWidth={true}
                actionItem={openDialogActionItem}
                reopenTrigger={reopenTrigger}
                onOpen={refetch}
            />
            {dialog}
        </>
    )
}

export default PriorityGroupSelector
